import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip
} from '@mui/material';
import { Edit, Delete, Visibility, ArrowBack, Login } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { superAdminAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

const PLAN_BG = { trial: '#fef3c7', basic: '#ede9fe', pro: '#d1fae5' };
const PLAN_TEXT = { trial: '#92400e', basic: '#5b21b6', pro: '#065f46' };

const HEALTH_BAND_COLOR = {
  critical:     { bg: '#fee2e2', text: '#991b1b' },
  dormant:      { bg: '#f3f4f6', text: '#374151' },
  at_risk:      { bg: '#fef3c7', text: '#92400e' },
  provisioning: { bg: '#e0e7ff', text: '#3730a3' },
  healthy:      { bg: '#d1fae5', text: '#065f46' },
};

const HEALTH_BAND_LABEL = {
  critical: 'Critical', dormant: 'Dormant', at_risk: 'At risk',
  provisioning: 'Provisioning', healthy: 'Healthy',
};

const formatBytes = (bytes) => {
  const value = Number(bytes) || 0;
  if (value <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / Math.pow(1024, exponent)).toFixed(exponent === 0 ? 0 : 2)} ${units[exponent]}`;
};

const timeAgo = (dateStr) => {
  if (!dateStr) return 'Never';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 2) return 'yesterday';
  return `${Math.floor(diff / 86400)}d ago`;
};

const Schools = () => {
  const navigate = useNavigate();
  const { user, logout, startImpersonation, isImpersonating } = useAuth();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, schoolId: null });
  const [otpDialog, setOtpDialog] = useState({ open: false, schoolId: null, code: '', requesting: false, verifying: false });
  const [impersonatingId, setImpersonatingId] = useState(null);

  const handleImpersonate = async (school) => {
    if (isImpersonating()) {
      toast.warning('You are already in an impersonation session. Exit it first.');
      return;
    }
    setImpersonatingId(school.id);
    const result = await startImpersonation(school.id);
    setImpersonatingId(null);

    if (result.success) {
      toast.success(`Viewing as ${school.name}`);
      navigate('/principal/dashboard');
    } else {
      toast.error(result.error);
    }
  };

  const fetchSchools = async () => {
    try {
      const [schoolsRes, subsRes, storageRes, healthRes] = await Promise.all([
        superAdminAPI.getSchools(),
        superAdminAPI.getSubscriptions().catch(() => null),
        superAdminAPI.getStorage().catch(() => null),
        superAdminAPI.getHealth().catch(() => null),
      ]);

      const subsBySchool = {};
      (subsRes?.data?.subscriptions ?? []).forEach((s) => { subsBySchool[s.school_id] = s; });
      const storageBySchool = {};
      (storageRes?.data?.storage?.schools ?? []).forEach((s) => { storageBySchool[s.schoolId] = s; });
      const healthBySchool = {};
      (healthRes?.data?.schools ?? []).forEach((s) => { healthBySchool[s.school_id] = s; });

      const merged = (schoolsRes.data.schools ?? []).map((school) => ({
        ...school,
        plan_tier: subsBySchool[school.id]?.plan_tier ?? null,
        student_count: parseInt(subsBySchool[school.id]?.student_count) || 0,
        storage_bytes: storageBySchool[school.id]?.totalBytes ?? 0,
        last_active_at: healthBySchool[school.id]?.last_active_at ?? null,
        health_score: healthBySchool[school.id]?.score ?? null,
        health_band: healthBySchool[school.id]?.band ?? null,
      }));

      setSchools(merged);
    } catch (error) {
      toast.error('Failed to load schools');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchools();
  }, []);

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(column);
      setOrder('asc');
    }
  };

  const sortedSchools = useMemo(() => {
    const list = [...schools];
    const dir = order === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      let av = a[orderBy];
      let bv = b[orderBy];
      if (orderBy === 'name') { av = av?.toLowerCase() ?? ''; bv = bv?.toLowerCase() ?? ''; }
      if (orderBy === 'last_active_at') { av = av ? new Date(av).getTime() : 0; bv = bv ? new Date(bv).getTime() : 0; }
      if (av == null) av = -Infinity;
      if (bv == null) bv = -Infinity;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return list;
  }, [schools, orderBy, order]);

  const handleDeleteClick = (schoolId) => {
    setDeleteDialog({ open: true, schoolId });
  };

  const handleDeleteConfirm = async () => {
    const schoolId = deleteDialog.schoolId;
    setDeleteDialog({ open: false, schoolId: null });
    await requestOtp(schoolId);
  };

  const requestOtp = async (schoolId) => {
    setOtpDialog({ open: true, schoolId, code: '', requesting: true, verifying: false });
    try {
      await superAdminAPI.requestDeleteOtp(schoolId);
      toast.info('An OTP has been sent to confirm this deletion');
    } catch (error) {
      toast.error('Failed to send deletion OTP');
      console.error(error);
      setOtpDialog({ open: false, schoolId: null, code: '', requesting: false, verifying: false });
      return;
    }
    setOtpDialog((prev) => ({ ...prev, requesting: false }));
  };

  const handleOtpCancel = () => {
    setOtpDialog({ open: false, schoolId: null, code: '', requesting: false, verifying: false });
  };

  const handleOtpVerify = async () => {
    setOtpDialog((prev) => ({ ...prev, verifying: true }));
    try {
      await superAdminAPI.deleteSchool(otpDialog.schoolId, otpDialog.code);
      toast.success('School deleted successfully');
      setOtpDialog({ open: false, schoolId: null, code: '', requesting: false, verifying: false });
      fetchSchools();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid or expired OTP');
      console.error(error);
      setOtpDialog((prev) => ({ ...prev, verifying: false }));
    }
  };

  const handleStatusToggle = async (schoolId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
      
      // Show confirmation dialog for making school inactive
      if (newStatus === 'Inactive') {
        const confirmed = window.confirm(
          'Are you sure you want to make this school Inactive?\n\n' +
          'This will prevent all principals and teachers from this school from logging in.'
        );
        if (!confirmed) return;
      }
      
      await superAdminAPI.updateSchoolStatus(schoolId, newStatus);
      toast.success(`School status updated to ${newStatus}`);
      if (newStatus === 'Inactive') {
        toast.info('All principals and teachers from this school are now blocked from logging in', {
          autoClose: 7000
        });
      }
      fetchSchools();
    } catch (error) {
      toast.error('Failed to update school status');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Manage Schools
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/superadmin/dashboard')}
          >
            Back to Dashboard
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/superadmin/schools/new')}
          >
            Add New School
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'name' ? order : false}>
                <TableSortLabel active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'} onClick={() => handleSort('name')}>
                  <strong>School Name</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'plan_tier' ? order : false}>
                <TableSortLabel active={orderBy === 'plan_tier'} direction={orderBy === 'plan_tier' ? order : 'asc'} onClick={() => handleSort('plan_tier')}>
                  <strong>Plan</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sortDirection={orderBy === 'student_count' ? order : false}>
                <TableSortLabel active={orderBy === 'student_count'} direction={orderBy === 'student_count' ? order : 'asc'} onClick={() => handleSort('student_count')}>
                  <strong>Students</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sortDirection={orderBy === 'storage_bytes' ? order : false}>
                <TableSortLabel active={orderBy === 'storage_bytes'} direction={orderBy === 'storage_bytes' ? order : 'asc'} onClick={() => handleSort('storage_bytes')}>
                  <strong>Storage</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'last_active_at' ? order : false}>
                <TableSortLabel active={orderBy === 'last_active_at'} direction={orderBy === 'last_active_at' ? order : 'asc'} onClick={() => handleSort('last_active_at')}>
                  <strong>Last active</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'health_score' ? order : false}>
                <TableSortLabel active={orderBy === 'health_score'} direction={orderBy === 'health_score' ? order : 'asc'} onClick={() => handleSort('health_score')}>
                  <strong>Health</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedSchools.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" py={3}>
                    No schools found. Click "Add New School" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedSchools.map((school) => (
                <TableRow key={school.id} hover>
                  <TableCell>{school.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={school.plan_tier ?? 'trial'}
                      size="small"
                      sx={{ fontSize: '0.7rem', bgcolor: PLAN_BG[school.plan_tier] ?? '#f3f4f6', color: PLAN_TEXT[school.plan_tier] ?? '#6b7280' }}
                    />
                  </TableCell>
                  <TableCell align="right">{school.student_count.toLocaleString('en-IN')}</TableCell>
                  <TableCell align="right">{formatBytes(school.storage_bytes)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{timeAgo(school.last_active_at)}</Typography>
                  </TableCell>
                  <TableCell>
                    {school.health_band ? (
                      <Chip
                        label={HEALTH_BAND_LABEL[school.health_band] ?? school.health_band}
                        size="small"
                        sx={{
                          fontSize: '0.7rem',
                          bgcolor: (HEALTH_BAND_COLOR[school.health_band] ?? {}).bg,
                          color: (HEALTH_BAND_COLOR[school.health_band] ?? {}).text,
                        }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={school.status}
                      color={school.status === 'Active' ? 'success' : 'error'}
                      size="small"
                      onClick={() => handleStatusToggle(school.id, school.status)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="info"
                      onClick={() => navigate(`/superadmin/schools/${school.id}`)}
                      title="View Details"
                    >
                      <Visibility />
                    </IconButton>
                    <Tooltip title={school.status === 'Inactive' ? 'School is inactive' : 'View as this school'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleImpersonate(school)}
                          disabled={impersonatingId === school.id || school.status === 'Inactive'}
                          sx={{ color: '#7C3AED' }}
                        >
                          <Login fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => navigate(`/superadmin/schools/${school.id}/edit`)}
                      title="Edit"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(school.id)}
                      title="Delete"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, schoolId: null })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this school? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, schoolId: null })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* OTP Confirmation Dialog */}
      <Dialog
        open={otpDialog.open}
        onClose={otpDialog.verifying ? undefined : handleOtpCancel}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Enter Deletion OTP</DialogTitle>
        <DialogContent>
          {otpDialog.requesting ? (
            <Box display="flex" alignItems="center" gap={2} py={2}>
              <CircularProgress size={20} />
              <Typography>Sending OTP...</Typography>
            </Box>
          ) : (
            <>
              <Typography sx={{ mb: 2 }}>
                An OTP has been sent to the configured verification email. Enter it below to permanently delete this school. This action cannot be undone.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                label="6-digit OTP"
                value={otpDialog.code}
                onChange={(e) =>
                  setOtpDialog((prev) => ({ ...prev, code: e.target.value.replace(/\D/g, '').slice(0, 6) }))
                }
                inputProps={{ maxLength: 6, inputMode: 'numeric' }}
              />
              <Button
                size="small"
                onClick={() => requestOtp(otpDialog.schoolId)}
                disabled={otpDialog.verifying}
                sx={{ mt: 1 }}
              >
                Resend OTP
              </Button>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOtpCancel} disabled={otpDialog.verifying}>
            Cancel
          </Button>
          <Button
            onClick={handleOtpVerify}
            color="error"
            variant="contained"
            disabled={otpDialog.requesting || otpDialog.verifying || otpDialog.code.length !== 6}
          >
            {otpDialog.verifying ? 'Verifying...' : 'Verify & Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Schools;
